import graphql__ba70b747e4cba0b261c4 from "./__generated__/EmailLayoutDetailsViewUpdateHtmlMutation.graphql.ts";
import graphql__261fbed7962a5d4004f7 from "./__generated__/EmailLayoutDetailsViewUpdateTextMutation.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import {graphql, useMutation} from "react-relay";
import Panel from "./presentational/Panel";
import Headline from "./presentational/Headline";
import ActionButton from "./presentational/button/ActionButton";
import {faSave, faStar, faTrash} from "@fortawesome/pro-regular-svg-icons";
import CodeMirrorEditor from "./editor/CodeMirrorEditor";
import SetEmailLayoutAsDefaultDialog from "./dialog/SetEmailLayoutAsDefaultDialog";
import DeleteEmailLayoutDialog from "./dialog/DeleteEmailLayoutDialog";

const EmailLayoutDetailsView: React.FC = () => {
    const intl = useIntl();
    const context = useOutletContext<any>();
    const emailLayout = context.emailLayout;

    const [htmlContent, setHtmlContent] = useState(emailLayout.htmlContent || '');
    const [textContent, setTextContent] = useState(emailLayout.textContent || '');
    const [hasHtmlChanges, setHasHtmlChanges] = useState(false);
    const [hasTextChanges, setHasTextChanges] = useState(false);
    const [showSetDefaultDialog, setShowSetDefaultDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [commitUpdateHtmlMutation, isUpdatingHtml] = useMutation(graphql__ba70b747e4cba0b261c4);

    const [commitUpdateTextMutation, isUpdatingText] = useMutation(graphql__261fbed7962a5d4004f7);

    const handleHtmlContentChange = useCallback((value: string) => {
        setHtmlContent(value);
        setHasHtmlChanges(value !== emailLayout.htmlContent);
    }, [emailLayout.htmlContent]);

    const handleTextContentChange = useCallback((value: string) => {
        setTextContent(value);
        setHasTextChanges(value !== emailLayout.textContent);
    }, [emailLayout.textContent]);

    const handleSaveHtml = useCallback(() => {
        commitUpdateHtmlMutation({
            variables: {
                input: {
                    id: emailLayout.emailLayoutIdentifier,
                    htmlContent: htmlContent,
                }
            },
            onCompleted: () => {
                setHasHtmlChanges(false);
            },
        });
    }, [commitUpdateHtmlMutation, emailLayout.emailLayoutIdentifier, htmlContent]);

    const handleSaveText = useCallback(() => {
        commitUpdateTextMutation({
            variables: {
                input: {
                    id: emailLayout.emailLayoutIdentifier,
                    textContent: textContent,
                }
            },
            onCompleted: () => {
                setHasTextChanges(false);
            },
        });
    }, [commitUpdateTextMutation, emailLayout.emailLayoutIdentifier, textContent]);

    return (
        <>
        <Panel>
            <div className="px-6 pt-6 pb-4">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <Headline>{intl.formatMessage({id: 'view.emailLayout.htmlContent'})}</Headline>
                    </div>
                    <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                        <ActionButton
                            icon={faSave}
                            title={intl.formatMessage({id: 'action.save'})}
                            onClick={handleSaveHtml}
                            disabled={!hasHtmlChanges || isUpdatingHtml}
                        />
                    </div>
                </div>
            </div>
            <div className="px-6 pb-6">
                <p className="text-sm text-gray-500 mb-4">
                    {intl.formatMessage({id: 'view.emailLayout.htmlContentDescription'})}
                </p>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                    <CodeMirrorEditor
                        value={htmlContent}
                        onChange={handleHtmlContentChange}
                        language="html"
                        height="800px"
                    />
                </div>
                <div className="mt-4 text-sm text-gray-500">
                    <p className="font-medium mb-2">{intl.formatMessage({id: 'view.emailLayout.availableVariables'})}</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><code className="bg-gray-100 px-1 rounded-sm">{`{{content}}`}</code> - {intl.formatMessage({id: 'view.emailLayout.variable.content'})}</li>
                        <li><code className="bg-gray-100 px-1 rounded-sm">{`{{footer}}`}</code> - {intl.formatMessage({id: 'view.emailLayout.variable.footerNote'})}</li>
                    </ul>
                </div>
            </div>
        </Panel>

        <Panel>
            <div className="px-6 pt-6 pb-4">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <Headline>{intl.formatMessage({id: 'view.emailLayout.textContent'})}</Headline>
                    </div>
                    <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                        <ActionButton
                            icon={faSave}
                            title={intl.formatMessage({id: 'action.save'})}
                            onClick={handleSaveText}
                            disabled={!hasTextChanges || isUpdatingText}
                        />
                    </div>
                </div>
            </div>
            <div className="px-6 pb-6">
                <p className="text-sm text-gray-500 mb-4">
                    {intl.formatMessage({id: 'view.emailLayout.textContentDescription'})}
                </p>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                    <CodeMirrorEditor
                        value={textContent}
                        onChange={handleTextContentChange}
                        language="text"
                        theme="light"
                        height="400px"
                    />
                </div>
                <div className="mt-4 text-sm text-gray-500">
                    <p className="font-medium mb-2">{intl.formatMessage({id: 'view.emailLayout.availableVariables'})}</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><code className="bg-gray-100 px-1 rounded-sm">{`{{content}}`}</code> - {intl.formatMessage({id: 'view.emailLayout.variable.content'})}</li>
                    </ul>
                </div>
            </div>
        </Panel>

        <div className="flex space-x-4 justify-end">
            {!emailLayout.isDefault && (
                <ActionButton
                    onClick={() => setShowSetDefaultDialog(true)}
                    icon={faStar}
                    label={intl.formatMessage({id: 'action.setAsDefault'})}
                />
            )}
            {!emailLayout.isDefault && (
                <ActionButton
                    onClick={() => setShowDeleteDialog(true)}
                    icon={faTrash}
                    label={intl.formatMessage({id: 'action.delete'})}
                />
            )}
        </div>

        {showSetDefaultDialog && (
            <SetEmailLayoutAsDefaultDialog
                emailLayout={emailLayout}
                onClose={() => setShowSetDefaultDialog(false)}
            />
        )}

        {showDeleteDialog && (
            <DeleteEmailLayoutDialog
                emailLayout={emailLayout}
                onClose={() => setShowDeleteDialog(false)}
            />
        )}
    </>
    );
}

export default EmailLayoutDetailsView;
